import { Button } from '@tempo-io/sui-button';
import { CalendarToday } from '@tempo-io/sui-icons';
import { Popover, PopoverContent, PopoverTrigger } from '@tempo-io/sui-popover';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DatePicker } from '@/common/components/date-picker';
import { formatLocalDate, toISODateString } from '@/common/utils/dateFormats';
import { EmptyFieldValue } from '@/features/items/components/item-field-value/EmptyFieldValue';
import { FieldWithStatusIndicator } from '@/features/items/components/item-field-value/FieldWithStatusIndicator';

export const DateField = ({
  id,
  name,
  value,
  isEditable = false,
  autoFocus = false,
  onChange,
  isPending,
  isSuccess,
  isError,
  error,
}: {
  id: string;
  name: string;
  value?: string;
  isEditable?: boolean;
  autoFocus?: boolean;
  onChange?: (value: string | null) => void;
  isPending?: boolean;
  isSuccess?: boolean;
  isError?: boolean;
  error?: Error | null;
}) => {
  const { t } = useTranslation();
  const inputId = `date-field-${id}`;
  const [open, setOpen] = useState(autoFocus);
  const formattedDate = value ? formatLocalDate(value) : '';

  const handleSelect = (date: string | null) => {
    onChange?.(toISODateString(date));
    setOpen(false);
  };

  return (
    <FieldWithStatusIndicator
      inputId={inputId}
      name={name}
      isPending={isPending}
      isSuccess={isSuccess}
    >
      {isEditable ? (
        <div className="flex flex-col gap-1">
          <Popover modal open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
              <Button
                id={inputId}
                variant="outline"
                className="h-10 w-full justify-start rounded-md border bg-bg-surface-base px-3 py-2 text-left font-normal"
                disabled={isPending}
              >
                <CalendarToday />
                {formattedDate ||
                  t(($) => $.adaptivePlanner.itemPanel.customFields.dateField.selectDate)}
              </Button>
            </PopoverTrigger>
            <PopoverContent>
              <DatePicker value={value} onSelect={handleSelect} showClearButton />
            </PopoverContent>
          </Popover>
          {isError && error && (
            <span className="text-xs text-text-error" data-testid="date-field-error">
              {error.message}
            </span>
          )}
        </div>
      ) : (
        <div className="text-sm text-text-neutral-primary" data-testid="date-field-value">
          {value ? formattedDate : <EmptyFieldValue />}
        </div>
      )}
    </FieldWithStatusIndicator>
  );
};
